import { cloneDeep } from 'lodash'
import OLLayerImage from 'ol/layer/Image'
import OLSourceImageWMS from 'ol/source/ImageWMS'

import type ImageWrapper from 'ol/Image'
import type { Options as ImageWMSOptions } from 'ol/source/ImageWMS'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import type { ConfigLayerBaseImageOptions } from 'config/ConfigLayerBaseImageOptions'
import { MapLayerWMS } from 'config/MapLayerWMS'
import { setLayerLoadDone, setLayerLoadFailed, setLayerLoadInProgress } from 'drawer/tabs/layers/actions'
import { loadImage } from 'utils/Image'
import { getMap } from 'utils/MapUtils'

import SetAttributions from './SetAttributions'

export interface ConfigLayerImageWMSOptions extends ConfigLayerOptions {
  config: ImageWMSOptions
}

export class MapLayerImageWMS extends MapLayerWMS {
  static createLayer (layerConf: ConfigLayerImageWMSOptions): OLLayerImage<OLSourceImageWMS> {
    this.preLayer(layerConf)

    // Deep clone layerConf object to avoid potential accidental side effects
    const clonedLayerConf = cloneDeep(layerConf)

    // Make sure all params are upper case. This is needed for automatically
    // deriving the legend URL from the OL Source
    const { params = {}, ...restOfLayerConfig } = clonedLayerConf.config
    const paramsUC: Record<string, number | string> = {}
    for (const [key, value] of Object.entries(params)) {
      paramsUC[key.toUpperCase()] = value
    }

    const olSourceOptions = {
      params: paramsUC,
      ...restOfLayerConfig
    }

    const olSourceImageWMS = new OLSourceImageWMS(olSourceOptions)
    const baseImageOptions: ConfigLayerBaseImageOptions = {
      title: layerConf.title,
      titleid: layerConf.titleid,
      group: layerConf.group,
      maxResolution: layerConf.maxResolution || 100000,
      maxZoom: layerConf.maxZoom,
      minResolution: layerConf.minResolution || 0.0001,
      minZoom: layerConf.minZoom,
      opacity: layerConf.opacity || 1,
      source: olSourceImageWMS,
      visible: PoziUtils.isLayerSelected(layerConf, true)
    }
    const olLayer = new OLLayerImage(baseImageOptions)

    const { titleid } = layerConf

    olSourceImageWMS.setImageLoadFunction(async function (image: ImageWrapper, url: string) {
      // Check if map is animating while we are loading
      const view = getMap().getView()
      const isAnimating = view.getAnimating()
      const zoom = view.getZoom()

      // console.debug('MapLayerImageWMS - tileLoadFunction', titleid, isAnimating, zoom, tile, url)

      // Do not request tiles while we are zooming in/out
      // (we will wait and check if the tile is still needed)
      // if (isAnimating) {
      //   console.log('MapLayerImageWMS - setImageLoadFunction - map is animating - pausing loading tile 250ms', isAnimating, zoom, titleid, url)
      //   await PoziUtils.sleep(250) // same duration as an OL zoom animation

      //   const newZoom = view.getZoom()
      //   if (newZoom !== zoom) {
      //     console.log('MapLayerImageWMS - setImageLoadFunction - zoom changed after slight delay - not loading tile', newZoom, zoom, titleid, url)
      //     return
      //   }
      // } else {
      //   // console.debug('MapLayerTileWMS - tileLoadFunction - map not animating. All good. :-)', isAnimating, zoom, titleid)
      // }

      try {
        window.store.dispatch(setLayerLoadInProgress(layerConf.titleid))

        const data = await loadImage(url)
        if (data !== undefined) {
          // @ts-expect-error
          image.getImage().src = URL.createObjectURL(data)
        } else {
          // image.setState(ImageState.ERROR) // ImageWrapper does not support setState (as of v7.1.0)
        }
        window.store.dispatch(setLayerLoadDone(layerConf.titleid))
      } catch (ex) {
        // image.setState(ImageState.ERROR) // ImageWrapper does not support setState (as of v7.1.0)
        window.store.dispatch(setLayerLoadFailed(layerConf.titleid))
      }
    })

    PoziUtils.setSourceLoaded(olLayer, olSourceImageWMS, layerConf)

    // Keep track of loading of the layer
    olSourceImageWMS.on('imageloadstart', () => {
      window.store.dispatch(setLayerLoadInProgress(layerConf.titleid))
    })

    // Mark layer as loaded in the old state model
    olSourceImageWMS.once('imageloadend', () => {
      Model.set('app.config.layerbytitleid[' + layerConf.titleid + '].loaded', true)
    })

    olSourceImageWMS.on(['imageloaderror', 'imageloadend'], () => {
      window.store.dispatch(setLayerLoadDone(layerConf.titleid))
    })

    olSourceImageWMS.on('imageloaderror', (event) => {
      const url = PoziUtils.getLayerUrl(layerConf)

      PoziUtils.gtag({
        category: 'data',
        status: 'warning',
        identifier: layerConf.title,
        action: 'ajax request fail',
        additionalInfo: url
      })

      console.error('MapLayerImageWMS - image load error for layer ' + layerConf.title, event)
    })

    // Automatically derive the legend URL from the layer configuration, if not provided yet
    this.autoGenerateLegendUrl(layerConf, olLayer)

    // Add attributions for Nearmap layers
    // TODO: do this in a better way
    const url = PoziUtils.getLayerUrl(layerConf) || ''
    if (olSourceImageWMS && url.includes('nearmap.com')) {
      const attributions = [
        '<a target="_blank" rel="noopener noreferrer" href="https://nearmap.com">© Nearmap</a>'
      ]

      SetAttributions(olLayer, attributions, true)
    }

    return olLayer
  }
}
